<template>
  <modal-wrap :name="modalName" :hide-footer="true" size="xl" dialog-class="modal-picker">
    <template #modal-title>Address Book</template>
    <template #default>
      <div class="mb-3">
        <input
          v-model="search"
          type="text"
          class="form-control form-control-sm"
          style="width: 220px"
          placeholder="Search..."
          @input="onSearch"
        />
      </div>
      <easy-data-table
        ref="dataTable"
        v-model:server-options="serverOptions"
        table-class-name="vue3-easy-data-table table-shadow no-overflow"
        :server-items-length="serverItemsLength"
        :loading="loading"
        :headers="headers"
        :items="items"
        :fixed-header="false"
        no-hover
        alternating
        hide-footer
        @update:server-options="updateServerOptions"
      >
        <template #loading>
          <b-spinner class="mt-5" variant="success" />
        </template>
        <template #no-data>
          <div style="text-align: center">No data available</div>
        </template>
        <template #item="{ column, item }: { column: string; item: AddressBook }">
          <span v-if="column === 'name'">{{ [item.firstName, item.lastName].filter(Boolean).join(' ') || '—' }}</span>
          <span v-if="column === 'company'">{{ item.company || '—' }}</span>
          <span v-if="column === 'countryCode'">{{ item.countryCode }}</span>
          <span v-if="column === 'city'">{{ item.city || '—' }}</span>
          <span v-if="column === 'addressLine'">{{ item.addressLine || '—' }}</span>
          <span v-if="column === 'phone'">{{ item.phoneNumber || '—' }}</span>
          <span v-if="column === 'email'">{{ item.email || '—' }}</span>
          <span v-if="column === 'select'">
            <b-button size="sm" variant="success" @click="selectAddress(item)">Select</b-button>
          </span>
        </template>
      </easy-data-table>
      <TablePagination
        :server-items-length="serverItemsLength"
        :server-options-ref="serverOptions"
        :data-table-ref="dataTable"
      />
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { AddressBookPickerModalProps } from '@/types/Components/Modals'
import type { AddressBook } from '@/types/Models/AddressBook'
import { BButton, BSpinner } from 'bootstrap-vue-next'
import { defineComponent, computed, watch, type ComputedRef } from 'vue'
import { useAddressBookTable } from '@/composables/AddressBook/useAddressBookTable'
import { ADDRESS_BOOK_PICKER } from '@/const/ModalTypes'
import { useVueNextModalStore } from '@/store/vueNextModal'
import EasyDataTable from 'vue3-easy-data-table'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'AddressBookPickerModal',
  components: { BButton, BSpinner, EasyDataTable, ModalWrap, TablePagination },
  setup() {
    const modalStore = useVueNextModalStore()

    const modalName = ADDRESS_BOOK_PICKER
    const modalData: ComputedRef<AddressBookPickerModalProps | undefined> = computed(
      () => modalStore.modalPropsByName(modalName),
    )
    const companyId: ComputedRef<string> = computed(() => modalData.value?.companyId ?? '')

    const table = useAddressBookTable(() => companyId.value, { watchCompanyId: false, type: 'recipient' })

    const selectAddress = (item: AddressBook) => {
      modalData.value?.onSelect?.(item)
      modalStore.closeModal(modalName)
    }

    const isOpen = computed(() => modalStore.isModalVisible(modalName))
    watch(isOpen, (open) => {
      if (open) {
        table.search.value = ''
        table.serverOptions.value = { page: 1, rowsPerPage: 50 }
        table.fetchData()
      }
    })

    return {
      ...table,
      headers: [...table.headers, { text: '', value: 'select' }],
      modalName,
      selectAddress,
    }
  },
})
</script>

<style>
.modal-picker {
  max-width: 90vw;
}
</style>
